import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { billingService } from '../../services/api';
import { INK, MUTED, RULE } from '../analytics/analyticsUi';
import { daysOverdue, overdueLabel, OVERDUE_COLOR } from '../../utils/overdue';

const STATUS = {
  draft: { label: 'Draft, not sent to the customer', color: '#8A94A6' },
  pending: { label: 'Awaiting payment', color: '#C77700' },
  paid: { label: 'Paid', color: '#1BAA52' },
  failed: { label: 'Payment failed', color: '#C2410C' },
  cancelled: { label: 'Cancelled', color: '#5B6577' }
};
const PAYMENT_METHODS = {
  manual: 'Bank transfer / manual',
  upi: 'UPI',
  net_banking: 'Net banking',
  credit_card: 'Credit card',
  debit_card: 'Debit card',
  wallet: 'Wallet',
  offline: 'Cash / cheque'
};

const money = (value, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', minimumFractionDigits: 2 }).format(value || 0);
const rate = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const day = (value) => (value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '–');
const monthLabel = (key) => new Date(`${key}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

export const invoiceTotal = (inv) => inv.total ?? (inv.amount + (inv.taxAmount || 0) - (inv.discountAmount || 0));

const Field = ({ label, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 1.5, py: 0.5 }}>
    <Typography variant="body2" sx={{ color: MUTED }}>{label}</Typography>
    <Box sx={{ color: INK, fontSize: '0.875rem', minWidth: 0, overflowWrap: 'anywhere' }}>{children}</Box>
  </Box>
);

/**
 * Full view of one invoice. Admin actions are shown only when the handlers are passed.
 * props: invoice, open, onClose, onPay, onApprove, onCancel
 */
const InvoiceDetailDialog = ({ invoice, open, onClose, onPay, onApprove, onCancel }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  if (!invoice) return null;

  const status = STATUS[invoice.status] || { label: invoice.status, color: MUTED };
  const overdue = daysOverdue(invoice);
  const items = invoice.items?.length
    ? invoice.items
    : [{ name: invoice.description || `${invoice.billing?.plan || 'Subscription'} plan`, quantity: 1, unitPrice: invoice.amount, amount: invoice.amount }];
  const paidItems = items.filter((i) => i.amount > 0 || !/\(free\)/.test(i.name));
  const freeItems = items.filter((i) => i.amount === 0 && /\(free\)/.test(i.name));
  const taxLines = invoice.taxLines?.length
    ? invoice.taxLines
    : (invoice.taxAmount ? [{ label: `Tax ${invoice.taxPercentage || ''}%`, amount: invoice.taxAmount }] : []);
  const account = invoice.whatsappAccount;
  const isOpen = ['pending', 'failed', 'draft'].includes(invoice.status);

  const itemRows = (list, muted) => list.map((item, index) => (
    <TableRow key={`${item.name}-${index}`} sx={{ '& td': { borderColor: RULE, fontVariantNumeric: 'tabular-nums', color: muted ? MUTED : INK } }}>
      <TableCell sx={{ pl: 0 }}>
        <Typography variant="body2" sx={{ color: 'inherit', fontWeight: muted ? 400 : 500 }}>{item.name}</Typography>
        {item.description && <Typography variant="caption" sx={{ color: MUTED }}>{item.description}</Typography>}
      </TableCell>
      <TableCell align="right">{Number(item.quantity ?? 1).toLocaleString('en-IN')}</TableCell>
      <TableCell align="right">{rate(item.unitPrice ?? item.amount)}</TableCell>
      <TableCell align="right" sx={{ pr: 0 }}>{money(item.amount, invoice.currency)}</TableCell>
    </TableRow>
  ));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen} aria-labelledby="invoice-detail-title">
      <DialogTitle id="invoice-detail-title" sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography component="span" sx={{ display: 'block', fontSize: '1.25rem', fontWeight: 700, color: INK }}>{invoice.invoiceNumber}</Typography>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: overdue ? OVERDUE_COLOR : status.color }} />
            <Typography variant="body2" sx={{ color: INK }}>{status.label}</Typography>
            {overdue > 0 && <Typography variant="body2" sx={{ color: OVERDUE_COLOR, fontWeight: 600 }}>{overdueLabel(overdue)}</Typography>}
          </Box>
        </Box>
        <IconButton onClick={onClose} aria-label="Close invoice" size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent>
        {invoice.status === 'draft' && invoice.notes && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>{invoice.notes}</Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4, rowGap: 2, mb: 3 }}>
          <Box>
            <Typography variant="caption" sx={{ color: MUTED, fontWeight: 600 }}>Billed to</Typography>
            <Typography sx={{ color: INK, fontWeight: 600, mt: 0.5 }}>{invoice.user?.company || invoice.user?.name || '–'}</Typography>
            {invoice.user?.company && <Typography variant="body2" sx={{ color: INK }}>{invoice.user.name}</Typography>}
            <Typography variant="body2" sx={{ color: MUTED }}>{invoice.user?.email}</Typography>
            {(invoice.buyerGstin || invoice.user?.gstin) && (
              <Typography variant="body2" sx={{ color: INK, mt: 0.5 }}>GSTIN {invoice.buyerGstin || invoice.user.gstin}</Typography>
            )}
            {account && (
              <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
                WhatsApp: {account.verifiedName || account.businessName}, {account.phoneNumber}
              </Typography>
            )}
          </Box>
          <Box>
            <Field label="Period">
              {invoice.billingMonth ? monthLabel(invoice.billingMonth) : `${day(invoice.billingPeriodStart)} to ${day(invoice.billingPeriodEnd)}`}
            </Field>
            <Field label="Issued">{day(invoice.createdAt)}</Field>
            <Field label="Due">
              <Box component="span" sx={{ color: overdue ? OVERDUE_COLOR : 'inherit', fontWeight: overdue ? 600 : 400 }}>{day(invoice.dueDate)}</Box>
            </Field>
            {invoice.placeOfSupply && <Field label="Place of supply">State code {invoice.placeOfSupply}</Field>}
            {invoice.sellerGstin && <Field label="Our GSTIN">{invoice.sellerGstin}</Field>}
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE } }}>
                <TableCell sx={{ pl: 0 }}>Description</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right" sx={{ pr: 0 }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itemRows(paidItems, false)}
              {itemRows(freeItems, true)}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ ml: 'auto', mt: 2, maxWidth: 340, fontVariantNumeric: 'tabular-nums' }}>
          {[['Subtotal', invoice.amount], ...taxLines.map((t) => [t.label, t.amount]),
            ...(invoice.discountAmount ? [['Discount', -invoice.discountAmount]] : [])].map(([label, value]) => (
            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2" sx={{ color: MUTED }}>{label}</Typography>
              <Typography variant="body2" sx={{ color: INK }}>{money(value, invoice.currency)}</Typography>
            </Box>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, mt: 0.5, borderTop: `1px solid ${RULE}` }}>
            <Typography sx={{ color: INK, fontWeight: 700 }}>Total</Typography>
            <Typography sx={{ color: INK, fontWeight: 700 }}>{money(invoiceTotal(invoice), invoice.currency)}</Typography>
          </Box>
        </Box>

        {invoice.status === 'paid' && (
          <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${RULE}` }}>
            <Typography variant="caption" sx={{ color: MUTED, fontWeight: 600 }}>Payment</Typography>
            <Field label="Paid on">{day(invoice.paidDate)}</Field>
            <Field label="Method">{PAYMENT_METHODS[invoice.paymentMethod] || invoice.paymentMethod || '–'}</Field>
            {invoice.transactionId && <Field label="Reference">{invoice.transactionId}</Field>}
          </Box>
        )}

        {invoice.notes && invoice.status !== 'draft' && (
          <Typography variant="body2" sx={{ color: MUTED, mt: 2 }}>{invoice.notes}</Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        {onCancel && invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
          <Button color="error" onClick={() => onCancel(invoice)} sx={{ textTransform: 'none', mr: 'auto' }}>Cancel invoice</Button>
        )}
        <Button
          component="a"
          href={billingService.generatePdfUrl(invoice._id)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: 'none' }}
        >
          Download PDF
        </Button>
        {onApprove && invoice.status === 'draft' && (
          <Button variant="outlined" onClick={() => onApprove(invoice)} sx={{ textTransform: 'none' }}>Approve and send</Button>
        )}
        {onPay && isOpen && (
          <Button variant="contained" onClick={() => onPay(invoice)} sx={{ textTransform: 'none', fontWeight: 600 }}>Mark as paid</Button>
        )}
        {!onPay && (
          <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDetailDialog;
